import PermissionService from "@/services/PermissionService";
import ListsService from "@/services/ListsService";
import RouteService from "@/services/RouteService";
import ProductNotice from "@/components/ProductNotice.vue";
import LlTooltip from "@/components/LlTooltip.vue";
import LlCheckbox from "@/components/LlCheckbox.vue";
import Company from "@/services/UserService/company";
import { useMeStore } from "@/services/UserService/useMeStore";
import MainHeader from "@/components/MainHeader.vue";
import SettingsSaveBar from "@/components/SettingsSaveBar.vue";
export default {
  name: "RolesSettings",
  components: {
    ProductNotice,
    LlTooltip,
    LlCheckbox,
    MainHeader,
    SettingsSaveBar
  },
  props: {
    rootScope: {
      type: Object,
      required: true
    },
    state: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      permissions: [],
      roles: [],
      formChanged: false,
      unsaved: false,
      saving: false,
      me: {},
      state: this.state
    };
  },
  computed: {
    userRoles() {
      return ["timekeeper", "paralegal", "lead", "principal", "operator", "accountant"];
    }
  },
  async mounted() {
    this.me = await this.userService.getMe();
    this.roles = this.userRoles.map(id => new ListsService().userRoles.find(role => role.id === id));
    this.permissionService = new PermissionService(this.userService);
    this.permissions = this.permissionService.permissions(this.me);
    this.presets = this.permissionService.presets(this.me);
    this.setPermissionsFromSettings(this.me.company.settings.permissions);
    this.routeService = new RouteService(this);
    const deactivate = this.routeService.rootScopeChange().$on("$stateChangeStart", event => {
      if (this.formChanged) {
        this.unsaved = true;
        setTimeout(() => {
          this.unsaved = false;
        }, 4000);
        event.preventDefault();
      } else {
        deactivate();
      }
    });
  },
  methods: {
    setPermissionsFromSettings(settings) {
      this.selectPreset(this.presets[0]);
      if (settings) {
        this.adjustFromSettings(settings.primary, 'primary');
        this.userRoles.forEach(role => this.adjustFromSettings(settings.roles[role], role));
      }
      this.formChanged = false;
    },
    handleClickFromCheckbox(value, obj) {
      const {
        permission,
        role,
        sub
      } = obj;
      if (obj.main) {
        if (permission.permissions) {
          this.permissions.find(p => p.id === permission.id).permissions.forEach(sub => {
            sub.roles[role.id] = value;
          });
        }
      } else {
        this.permissions.find(p => p.id === permission.id).permissions.find(per => per.id === sub.id).roles[role.id] = value;
      }
      this.formChanged = true;
      return permission.roles[role.id] = this.valueFromChildren(permission.permissions, role.id);
    },
    adjustFromSettings(setting, roleId) {
      this.permissions.forEach(permission => {
        permission.permissions.forEach(sub => {
          if (setting[permission.id] && setting[permission.id][sub.id] !== undefined) {
            sub.roles[roleId] = setting[permission.id][sub.id];
          }
          // backwards compatibility: these props were added later, so if they are
          // not in settings, we default them to true
          if (permission.id === 'charges' && !setting[permission.id] && sub.id !== 'lockTime') {
            sub.roles[roleId] = true;
          }
          // same role-based default PermissionConfig.cs applies (account.IsPrincipalOrOperator,
          // which includes accountant) for any firm that hasn't explicitly set this yet
          if (sub.id === 'lockTime' && setting[permission.id]?.[sub.id] === undefined) {
            sub.roles[roleId] = ['principal', 'operator', 'accountant'].indexOf(roleId) >= 0;
          }
          if (sub.id === 'assignToExpense' && setting[permission.id][sub.id] === undefined) {
            sub.roles[roleId] = true;
          }
          // default to the same role-based grant PermissionConfig.cs uses server-side
          // (account.IsPrincipalOrOperator) for any firm that hasn't explicitly set this yet
          if (sub.id === 'createHardCost' && setting[permission.id][sub.id] === undefined) {
            sub.roles[roleId] = ['principal', 'operator', 'accountant'].indexOf(roleId) >= 0;
          }
          // default dashboard/ai to follow the firms current setting for "other revenue reports"
          if (['chat', 'viewDashboards', 'editDashboards'].indexOf(sub.id) >= 0 && setting[permission.id][sub.id] === undefined) {
            sub.roles[roleId] = setting.reports?.revenue && (roleId === 'operator' || roleId === 'principal');
          }
        });
        permission.roles[roleId] = this.valueFromChildren(permission.permissions, roleId);
      });
    },
    selectPreset(preset) {
      this.formChanged = true;
      this.permissions.forEach(permission => {
        if (permission.permissions) {
          this.createRolesFromPreset(permission.permissions, preset, permission.id);
        }
        // This sets the top level role values to reflect child values:
        // true if all are set, false if none are set, and undefined if some are set and some are not
        permission.roles = this.roles.reduce((roles, role) => {
          roles[role.id] = this.valueFromChildren(permission.permissions, role.id);
          return roles;
        }, {});
      });
    },
    createRolesFromPreset(permissions, preset, parent) {
      permissions.forEach(permission => {
        permission.roles = this.roles.reduce((roles, role) => {
          // Look in the preset setting for either include/exclude and set the permission accordingly
          const setting = preset.permissions[role.id];
          const key = parent ? `${parent}.${permission.id}` : permission.id;
          roles[role.id] = setting.include ? setting.include.indexOf(key) >= 0 : setting.exclude.indexOf(key) < 0;
          return roles;
        }, {});
        if (permission.permissions) {
          this.createRolesFromPreset(permission.permissions, preset, permission.id);
        }
      });
    },
    valueFromChildren(permissions, roleId) {
      if (!permissions.find(p => p.roles[roleId])) {
        return false;
      }
      if (!permissions.find(p => !p.roles[roleId])) {
        return true;
      }
      return undefined;
    },
    save() {
      this.saving = true;
      const primary = this.settingsFromRolePermission(this.permissions, 'primary');
      const roles = this.userRoles.reduce((result, role) => ({
        ...result,
        [role]: this.settingsFromRolePermission(this.permissions, role)
      }), {});
      const company = new Company(this.leanLawApi);
      company.update({
        settings: {
          permissions: {
            primary,
            roles
          }
        }
      }).then(company => {
        this.setPermissionsFromSettings(company.settings.permissions);
        this.alerts.success(`Permissions saved`);
        this.userService.reload();
        // Vue pages read permissions from Pinia, which reload() doesn't touch.
        useMeStore().getMe(true);
      }).catch(err => {
        this.alerts.error(err);
      }).finally(() => {
        this.saving = false;
      });
    },
    settingsFromRolePermission(permissions, role) {
      return permissions.reduce((result, p) => {
        const subs = p.permissions.reduce((sr, s) => ({
          ...sr,
          [s.id]: s.roles[role]
        }), {});
        result[p.id] = subs;
        return result;
      }, {});
    },
    cancel() {
      this.setPermissionsFromSettings(this.me.company.settings.permissions);
    }
  }
};